import React from 'react';
import { motion } from 'framer-motion';
import { SlideWrapper, staggerContainer, fadeUpItem, scaleUpItem } from '../Shared';
import { Bot, Settings, BrainCircuit, Building2, User, Layers, Network, GitMerge } from 'lucide-react';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/v9ygAz6j1H3a29mCiVRRuK/vexu_logo.svg';
const NEXA_LOGO = 'https://cdn.magicpatterns.com/uploads/fKbS8KZb8wagSLFNLZymLJ/nexa42_png.png';
export function Slide6() {
  const features = [{
    icon: Bot,
    title: 'Core Capabilities',
    items: ['AI call handling', 'Omnichannel communication', 'Real-time understanding', 'Live user control']
  }, {
    icon: Settings,
    title: 'Automation',
    items: ['Executes tasks in conversations', 'Triggers workflows', 'Integrates via APIs & webhooks']
  }, {
    icon: BrainCircuit,
    title: 'Intelligence',
    items: ['Context-aware responses', 'Contact grouping & personalization', 'Calendar integration & scheduling']
  }];
  return <SlideWrapper data-id="element-98">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="h-full flex flex-col justify-center" data-id="element-99">
        <motion.div variants={fadeUpItem} className="mb-6 md:mb-10 flex items-center gap-3 md:gap-5" data-id="element-100">
          <img src={VEXU_LOGO} alt="Vexu AI" className="w-10 h-10 md:w-14 md:h-14 object-contain" data-id="element-101" />
          <div data-id="element-102">
            <h2 className="text-2xl md:text-4xl font-heading font-bold mb-1 text-charcoal-900" data-id="element-103">
              Vexu AI
            </h2>
            <p className="text-base md:text-xl text-vexu-600" data-id="element-104">
              Communication & Automation Engine
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-10" data-id="element-105">
          {features.map((feat, i) => <motion.div key={i} variants={scaleUpItem} className="p-5 md:p-8 rounded-2xl bg-white border border-gray-100 shadow-sm hover:border-vexu-300 hover:shadow-md transition-all" data-id="element-106">
              <feat.icon className="w-8 h-8 md:w-10 md:h-10 text-vexu-500 mb-4 md:mb-6" data-id="element-107" />
              <h3 className="text-xl md:text-2xl font-bold font-heading mb-4 md:mb-6 text-charcoal-900" data-id="element-108">
                {feat.title}
              </h3>
              <ul className="space-y-3 md:space-y-4" data-id="element-109">
                {feat.items.map((item, j) => <li key={j} className="flex items-start gap-2 md:gap-3 text-gray-700" data-id="element-110">
                    <div className="w-1.5 h-1.5 rounded-full bg-vexu-500 mt-2 md:mt-2.5 shrink-0" data-id="element-111" />
                    <span className="text-base md:text-lg" data-id="element-112">{item}</span>
                  </li>)}
              </ul>
            </motion.div>)}
        </div>

        {/* AI Conversation Workflow */}
        <motion.div variants={fadeUpItem} className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm" data-id="element-113">
          <div className="flex flex-col md:flex-row items-center md:justify-between gap-2" data-id="element-114">
            {[{
            label: '📞 Incoming Call',
            sub: 'Customer dials in',
            bg: 'bg-blue-50 border-blue-200'
          }, {
            label: '🤖 AI Processes',
            sub: 'Context analyzed',
            bg: 'bg-mint-100 border-vexu-200'
          }, {
            label: '💬 Smart Response',
            sub: 'Personalized reply',
            bg: 'bg-green-50 border-green-200'
          }, {
            label: '⚡ Action Triggered',
            sub: 'Workflow executed',
            bg: 'bg-amber-50 border-amber-200'
          }, {
            label: '✅ Task Complete',
            sub: 'Logged & tracked',
            bg: 'bg-vexu-500/10 border-vexu-300'
          }].map((step, i) => <div key={i} className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto" data-id="element-115">
                <div className={`px-4 py-3 rounded-xl border text-center w-full md:w-auto ${step.bg}`} data-id="element-116">
                  <p className="text-sm font-bold text-charcoal-900" data-id="element-117">
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-118">{step.sub}</p>
                </div>
                {i < 4 && <div className="text-vexu-400 text-lg font-bold hidden md:block" data-id="element-119">
                    →
                  </div>}
                {i < 4 && <div className="text-vexu-400 text-lg font-bold md:hidden" data-id="element-120">
                    ↓
                  </div>}
              </div>)}
          </div>
        </motion.div>
      </motion.div>
    </SlideWrapper>;
}
export function Slide7() {
  return <SlideWrapper className="items-center" data-id="element-121">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="w-full h-full flex flex-col md:pt-[3.75rem] md:pb-[3.75rem] items-center justify-start pt-[0px] pb-[48px]" data-id="element-122">
        <motion.div variants={fadeUpItem} className="flex items-center justify-center gap-3 md:gap-4 mb-2 text-center" data-id="element-123">
          <img src={VEXU_LOGO} alt="Vexu AI" className="w-8 h-8 md:w-12 md:h-12 object-contain" data-id="element-124" />
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-charcoal-900" data-id="element-125">
            Vexu AI Applications
          </h2>
        </motion.div>
        <motion.p variants={fadeUpItem} className="text-base md:text-xl text-gray-500 mb-6 md:mb-8 text-center" data-id="element-126">
          Built for both Enterprise and Personal use
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 flex-1 min-h-0 px-2 md:px-[5.4375rem] py-4 md:py-[1.25rem] gap-6 md:gap-[3.1875rem] pt-[0px] pb-[0px] pl-[47px] pr-[47px]" data-id="element-127">
          {/* B2B Enterprise */}
          <motion.div variants={scaleUpItem} className="rounded-2xl overflow-hidden flex flex-col min-h-0" data-id="element-128">
            <div className="flex-1 min-h-0 relative flex items-center justify-center" data-id="element-129">
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/qF6fRCc67tQ5Uom5WS2xew/17-3.png" alt="Vexu AI B2B Portal" className="w-full h-full object-contain" data-id="element-130" />
            </div>
            <div className="pt-3 shrink-0" data-id="element-131">
              <div className="flex gap-2 md:gap-3 mb-2 items-center justify-center" data-id="element-132">
                <Building2 className="w-5 h-5 md:w-7 md:h-7 text-vexu-500 shrink-0" data-id="element-133" />
                <h3 className="text-xl md:text-2xl font-bold font-heading text-charcoal-900" data-id="element-134">
                  B2B Enterprise Portal
                </h3>
              </div>
              <div className="flex flex-wrap gap-2 items-center justify-center" data-id="element-135">
                {['AI Call Center', 'Service Automation', 'Process Execution'].map((tag, i) => <span key={i} className="px-2 py-1 md:px-3 md:py-1 rounded-full bg-mint-100 text-vexu-600 text-xs md:text-sm font-medium" data-id="element-136">
                    {tag}
                  </span>)}
              </div>
            </div>
          </motion.div>

          {/* B2C Consumer */}
          <motion.div variants={scaleUpItem} className="rounded-2xl overflow-hidden flex flex-col min-h-0" data-id="element-137">
            <div className="flex-1 min-h-0 relative flex items-center justify-center" data-id="element-138">
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/qF6fRCc67tQ5Uom5WS2xew/17-6.png" alt="Vexu AI B2C App" className="w-full h-full object-contain" data-id="element-139" />
            </div>
            <div className="pt-3 shrink-0" data-id="element-140">
              <div className="flex gap-2 md:gap-3 mb-2 items-center justify-center" data-id="element-141">
                <User className="w-5 h-5 md:w-7 md:h-7 text-charcoal-800 shrink-0" data-id="element-142" />
                <h3 className="text-xl md:text-2xl font-bold font-heading text-charcoal-900" data-id="element-143">
                  B2C Consumer App
                </h3>
              </div>
              <div className="flex flex-wrap gap-2 items-center justify-center" data-id="element-144">
                {['Personal AI Assistant', 'Calls & Messages', 'Acts on Your Behalf'].map((tag, i) => <span key={i} className="px-2 py-1 md:px-3 md:py-1 rounded-full bg-gray-100 text-charcoal-800 text-xs md:text-sm font-medium" data-id="element-145">
                    {tag}
                  </span>)}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </SlideWrapper>;
}
export function Slide8() {
  const modules = ['Communication', 'Document Management', 'Digital Signature', 'Workflow Engine', 'Form Builder', 'Task Management'];
  return <SlideWrapper data-id="element-146">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="h-full flex flex-col gap-0 pt-[0px] pb-[0px] mt-[0px] mb-[0px] items-center justify-center" data-id="element-147">
        <motion.div variants={fadeUpItem} className="mb-6 md:mb-10 text-center flex flex-col items-center justify-center" data-id="element-148">
          <img src={NEXA_LOGO} alt="Nexa42" className="h-16 md:h-16 object-contain mb-2 md:mb-4 py-2 md:py-[0.75rem] pt-[19px] pb-[19px]" data-id="element-149" />
          <p className="text-gray-600 text-sm md:text-[1rem]" data-id="element-150">
            Unified Business Operating System
          </p>
        </motion.div>

        <motion.div variants={scaleUpItem} className="relative mb-8 md:mb-12" data-id="element-151">
          <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none hidden md:flex" data-id="element-152">
            <div className="w-3/4 h-1/2 border-2 border-gray-300 rounded-[6.25rem]" data-id="element-153" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 relative z-10 max-w-4xl mx-auto" data-id="element-154">
            {modules.map((mod, i) => <div key={i} className="p-4 md:p-6 rounded-2xl bg-white/90 backdrop-blur-md border border-gray-200 text-center shadow-sm hover:shadow-md transition-shadow pt-[6px] pb-[6px] pl-[26px] pr-[26px]" data-id="element-155">
                <Layers className="w-6 h-6 md:w-8 md:h-8 text-charcoal-700 mx-auto mb-2 md:mb-4 pt-[0.25rem] pb-[0.25rem]" data-id="element-156" />
                <h3 className="md:text-lg text-[#4B5563] text-[14px] font-[500]" data-id="element-157">
                  {mod}
                </h3>
              </div>)}
          </div>
        </motion.div>

        <motion.div variants={fadeUpItem} className="text-center mb-6 md:mb-10" data-id="element-158">
          <div className="inline-flex items-center gap-2 md:gap-3 px-4 py-2 md:px-6 md:py-3 rounded-full bg-white border border-gray-200 text-sm md:text-base font-medium tracking-wide shadow-sm text-[#4B5563]" data-id="element-159">
            <Network className="w-4 h-4 md:w-5 md:h-5 text-vexu-500" data-id="element-160" />
            All modules are natively built and connected
          </div>
        </motion.div>

        {/* Connected Modules Visual */}
        <motion.div variants={fadeUpItem} className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm pt-[0.75rem] pb-[0.75rem]" data-id="element-161">
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-3" data-id="element-162">
            {[{
            mod: '💬 Comm',
            color: 'bg-blue-50 border-blue-200'
          }, {
            mod: '📂 Docs',
            color: 'bg-purple-50 border-purple-200'
          }, {
            mod: '✍️ Sign',
            color: 'bg-amber-50 border-amber-200'
          }].map((m, i) => <div key={i} className="flex flex-col md:flex-row items-center gap-2 md:gap-3" data-id="element-163">
                <div className={`px-4 py-2 rounded-lg border text-sm font-semibold text-charcoal-800 ${m.color}`} data-id="element-164">
                  {m.mod}
                </div>
                {i < 2 && <div className="hidden md:block w-6 h-0.5 bg-vexu-400 rounded" data-id="element-165" />}
                {i < 2 && <div className="md:hidden w-0.5 h-4 bg-vexu-400 rounded" data-id="element-166" />}
              </div>)}
            <div className="my-2 md:my-0 md:mx-3 w-10 h-10 md:w-12 md:h-12 rounded-full bg-vexu-500 text-white flex items-center justify-center font-bold text-xs shadow-md" data-id="element-167">
              N42
            </div>
            {[{
            mod: '🔄 Flow',
            color: 'bg-green-50 border-green-200'
          }, {
            mod: '📝 Forms',
            color: 'bg-pink-50 border-pink-200'
          }, {
            mod: '✅ Tasks',
            color: 'bg-orange-50 border-orange-200'
          }].map((m, i) => <div key={i} className="flex flex-col md:flex-row items-center gap-2 md:gap-3" data-id="element-168">
                {i === 0 && <div className="hidden md:block w-6 h-0.5 bg-vexu-400 rounded" data-id="element-169" />}
                {i === 0 && <div className="md:hidden w-0.5 h-4 bg-vexu-400 rounded" data-id="element-170" />}
                <div className={`px-4 py-2 rounded-lg border text-sm font-semibold text-charcoal-800 ${m.color}`} data-id="element-171">
                  {m.mod}
                </div>
                {i < 2 && <div className="hidden md:block w-6 h-0.5 bg-vexu-400 rounded" data-id="element-172" />}
                {i < 2 && <div className="md:hidden w-0.5 h-4 bg-vexu-400 rounded" data-id="element-173" />}
              </div>)}
          </div>
          <p className="text-center text-xs md:text-sm text-vexu-600 font-medium mt-3" data-id="element-174">
            All modules flow through one unified core
          </p>
        </motion.div>
      </motion.div>
    </SlideWrapper>;
}
export function Slide9() {
  return <SlideWrapper data-id="element-175">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="h-full flex flex-col justify-center my-8 md:my-[4.5625rem]" data-id="element-176">
        <motion.div variants={fadeUpItem} className="mb-6 md:mb-8 flex items-center gap-3 md:gap-4" data-id="element-177">
          <img src={NEXA_LOGO} alt="Nexa42" className="w-8 h-8 md:w-12 md:h-12 object-contain rounded-lg" data-id="element-178" />
          <div data-id="element-179">
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-1 text-charcoal-900" data-id="element-180">
              Enterprise Architecture
            </h2>
            <p className="text-sm md:text-lg text-gray-500" data-id="element-181">
              Built for Real Organizational Structures
            </p>
          </div>
        </motion.div>

        {/* Org Hierarchy Chart */}
        <motion.div variants={scaleUpItem} className="flex-1 flex flex-col items-center justify-center min-h-0 overflow-x-auto pb-6" data-id="element-182">
          <div className="w-full max-w-5xl" data-id="element-183">
            {/* Level 1: Holding Group */}
            <div className="flex justify-center mb-1" data-id="element-184">
              <div className="px-4 py-2 md:px-8 md:py-3 rounded-xl bg-vexu-500 text-white font-bold text-sm md:text-lg font-heading shadow-lg text-center" data-id="element-185">
                🏢 Holding Group
              </div>
            </div>
            <div className="flex justify-center mb-1" data-id="element-186">
              <div className="w-0.5 h-4 md:h-6 bg-[#D7DBE3]" data-id="element-187" />
            </div>

            {/* Level 2: Subsidiaries */}
            <div className="relative flex justify-center mb-1" data-id="element-188">
              <div className="absolute top-0 left-1/4 right-1/4 h-0.5 bg-gray-300 ml-[1.25rem] mr-[1.25rem] md:ml-[2.375rem] md:mr-[2.375rem]" data-id="element-189" />
              <div className="flex gap-4 md:gap-12 relative" data-id="element-190">
                {['Subsidiary A', 'Subsidiary B'].map((sub, i) => <div key={i} className="flex flex-col items-center" data-id="element-191">
                    <div className="w-0.5 h-4 bg-gray-300" data-id="element-192" />
                    <div className="px-3 py-1.5 md:px-6 md:py-2.5 rounded-xl bg-white border-2 border-vexu-300 text-charcoal-900 font-semibold text-xs md:text-base shadow-sm text-center whitespace-nowrap" data-id="element-193">
                      🏬 {sub}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Connector lines to sub-subsidiaries */}
            <div className="flex justify-center mb-1" data-id="element-194">
              <div className="flex gap-4 md:gap-12 w-full max-w-lg" data-id="element-195">
                <div className="flex-1 flex justify-center" data-id="element-196">
                  <div className="w-0.5 h-4 md:h-5 bg-gray-300" data-id="element-197" />
                </div>
                <div className="flex-1 flex justify-center" data-id="element-198">
                  <div className="w-0.5 h-4 md:h-5 bg-gray-300" data-id="element-199" />
                </div>
              </div>
            </div>

            {/* Level 3: Sub-Subsidiaries */}
            <div className="flex justify-center mb-1" data-id="element-200">
              <div className="flex gap-1.5 md:gap-4 flex-wrap justify-center" data-id="element-201">
                {['Sub-Sub A1', 'Sub-Sub A2', 'Sub-Sub B1', 'Sub-Sub B2'].map((ss, i) => <div key={i} className="flex flex-col items-center" data-id="element-202">
                      <div className="px-2 py-1 md:px-4 md:py-2 rounded-lg bg-mint-100 border border-vexu-200 text-charcoal-800 font-medium text-[0.625rem] md:text-sm shadow-sm text-center whitespace-nowrap" data-id="element-203">
                        {ss}
                      </div>
                    </div>)}
              </div>
            </div>

            {/* Connector to Departments */}
            <div className="flex justify-center mb-1" data-id="element-204">
              <div className="w-0.5 h-4 md:h-5 bg-gray-200" data-id="element-205" />
            </div>

            {/* Level 4: Departments */}
            <div className="flex justify-center mb-1" data-id="element-206">
              <div className="flex gap-1.5 md:gap-3 flex-wrap justify-center" data-id="element-207">
                {['Finance', 'HR', 'Operations', 'Sales', 'IT', 'Marketing'].map((dept, i) => <div key={i} className="flex flex-col items-center" data-id="element-208">
                    <div className="px-2 py-1 md:px-4 md:py-2 rounded-lg bg-white border border-gray-200 text-gray-700 font-medium text-[0.625rem] md:text-sm shadow-sm text-center whitespace-nowrap" data-id="element-209">
                      📋 {dept}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Connector to Teams */}
            <div className="flex justify-center mb-1" data-id="element-210">
              <div className="w-0.5 h-4 md:h-5 bg-gray-200" data-id="element-211" />
            </div>

            {/* Level 5: Teams */}
            <div className="flex justify-center" data-id="element-212">
              <div className="flex gap-1.5 md:gap-3 flex-wrap justify-center" data-id="element-213">
                {['Team Alpha', 'Team Beta', 'Team Gamma', 'Team Delta', 'Team Epsilon'].map((team, i) => <div key={i} className="px-2 py-1 md:px-3 md:py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600 text-[0.625rem] md:text-xs font-medium shadow-sm whitespace-nowrap" data-id="element-214">
                    👥 {team}
                  </div>)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* What This Enables */}
        <motion.div variants={fadeUpItem} className="mt-6 flex justify-center gap-2 md:gap-4 flex-wrap" data-id="element-215">
          {['Central control from group level', 'Delegated execution across entities', 'Full visibility across all departments'].map((item, i) => <div key={i} className="flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full bg-white border border-gray-100 shadow-sm text-charcoal-800 text-xs md:text-sm font-medium" data-id="element-216">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-vexu-500 shrink-0" data-id="element-217" />
              {item}
            </div>)}
        </motion.div>
      </motion.div>
    </SlideWrapper>;
}
export function Slide10() {
  return <SlideWrapper data-id="element-218">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="h-full md:h-[calc(100svh-24px)] flex flex-col items-center justify-center" data-id="element-219">
        <motion.div variants={fadeUpItem} className="mb-8 text-center flex flex-col items-center" data-id="element-220">
          <img src={NEXA_LOGO} alt="Nexa42" className="w-8 h-8 md:w-12 md:h-12 object-contain rounded-lg mb-2 md:mb-3" data-id="element-221" />
          <h2 className="font-heading font-bold mb-1 md:mb-2 text-charcoal-900 text-2xl md:text-[1.5rem]" data-id="element-222">
            Execution Flow
          </h2>
          <p className="flex items-center justify-center gap-2 font-[300] text-[#333640] text-base md:text-[1.125rem]" data-id="element-223">
            How Work Happens in Nexa42
          </p>
        </motion.div>

        <motion.div variants={scaleUpItem} className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4 mb-8 relative" data-id="element-224">
          {[{
          step: '1',
          title: 'Group',
          desc: 'Assigns tasks to CEOs / GMs'
        }, {
          step: '2',
          title: 'GMs',
          desc: 'Assign to departments'
        }, {
          step: '3',
          title: 'Teams',
          desc: 'Execute the work'
        }].map((item, i) => <div key={i} className="relative z-10 flex flex-col items-center w-full md:w-1/3" data-id="element-225">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-white border-4 border-vexu-500 flex items-center justify-center text-xl md:text-2xl font-bold text-vexu-600 mb-3 md:mb-6 shadow-md" data-id="element-226">
                {item.step}
              </div>
              <div className="p-4 md:p-6 rounded-2xl bg-white/90 border border-gray-200 text-center w-full max-w-[17.5rem] backdrop-blur-sm shadow-sm" data-id="element-227">
                <h3 className="text-xl md:text-2xl font-bold font-heading mb-1 md:mb-2 text-charcoal-900" data-id="element-228">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600" data-id="element-229">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </motion.div>

        <motion.div variants={fadeUpItem} className="text-center mb-6 md:mb-10" data-id="element-230">
          <p className="text-sm md:text-xl text-vexu-700 inline-block px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-2xl border border-vexu-200 shadow-sm font-[400] bg-[#E8F5E900]" data-id="element-231">
            Tracked in one dashboard across the entire organization
          </p>
        </motion.div>

        {/* Mini Dashboard Mockup */}
        <motion.div variants={fadeUpItem} className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm" data-id="element-232">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-233">
            {[{
            label: 'Tasks Assigned',
            value: '142',
            change: '+12 today',
            color: 'text-vexu-600'
          }, {
            label: 'In Progress',
            value: '38',
            change: '27% of total',
            color: 'text-blue-600'
          }, {
            label: 'Completed',
            value: '96',
            change: '68% rate',
            color: 'text-green-600'
          }, {
            label: 'Overdue',
            value: '8',
            change: '↓ 3 from last week',
            color: 'text-red-500'
          }].map((stat, i) => <div key={i} className="p-3 rounded-xl bg-gray-50 border border-gray-100 text-center" data-id="element-234">
                <p className="text-xs text-gray-500 font-medium mb-1" data-id="element-235">
                  {stat.label}
                </p>
                <p className={`text-2xl font-bold font-heading ${stat.color}`} data-id="element-236">
                  {stat.value}
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-237">{stat.change}</p>
              </div>)}
          </div>
          <div className="mt-3 flex items-center gap-3" data-id="element-238">
            <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-239">
              <div className="h-full bg-vexu-500 rounded-l-full" style={{
              width: '68%'
            }} data-id="element-240" />
              <div className="h-full bg-blue-400" style={{
              width: '20%'
            }} data-id="element-241" />
              <div className="h-full bg-red-400 rounded-r-full" style={{
              width: '6%'
            }} data-id="element-242" />
            </div>
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap" data-id="element-243">
              Overall: 68% complete
            </span>
          </div>
        </motion.div>
      </motion.div>
    </SlideWrapper>;
}